'use strict';

import request from 'superagent';
import { sessionStorage } from './store';
import config from '../config';

const API_URL_BASE = (window && window.location.origin) + '/api';
const TOKEN_KEY = config.SESSION_TOKEN_KEY;
const LEVEL_KEY = config.SESSION_LEVEL_KEY;
const IS_STAFF_KEY = config.SESSION_IS_STAFF_KEY;
const PARTNER_CODE_KEY = config.SESSION_PARTNER_CODE_KEY;

const self = {
    authStatusHandlers: []
};

function setToken({ token, level, is_staff, partner_code }) {
    sessionStorage.setItem(TOKEN_KEY, token);
    sessionStorage.setItem(LEVEL_KEY, level);
    sessionStorage.setItem(IS_STAFF_KEY, is_staff ? '1' : '');
    sessionStorage.setItem(PARTNER_CODE_KEY, partner_code || '');
}

function getToken() {
    return sessionStorage.getItem(TOKEN_KEY);
}

/**
 * Returns the user's access level, as provided by the API at login time.
 */
function getLevel() {
    return sessionStorage.getItem(LEVEL_KEY);
}

function isStaff() {
    return !!sessionStorage.getItem(IS_STAFF_KEY);
}

function getPartnerCode() {
    return sessionStorage.getItem(PARTNER_CODE_KEY) || '';
}

function handleAuthStatusChange(status) {
    for (const handler of self.authStatusHandlers) {
        handler(status);
    }
}

function url(path) {
    return `${API_URL_BASE}${path}`;
}

/**
 * Authenticates the user against the API, starting the 2FA process in
 * the backend (generating and sending the 2FA code by email).
 * @param email
 * @param password
 * @returns {Promise<unknown>}
 */
function login(email, password) {
    const csrf = getCookie('csrftoken');

    return new Promise(function (resolve, reject) {
        request
            .post(url('/login/start/'))
            .type('json')
            .accept('json')
            .set('X-CSRFToken', csrf)
            .send({
                email: email,
                password: password
            })
            .end(function (err, res) {
                // In debug mode we alreay have a token at this point
                if (res && res.body && res.body.token) {
                    setToken(res.body);
                    handleAuthStatusChange(true);
                    resolve();
                    return;
                }
                if (err || !res || !res.body.status) {
                    reject((res && res.body) || {});
                } else {
                    resolve(res.body);
                }
            });
    });
}

/**
 * Completes the 2FA login by sending the code received by email.
 * @param email
 * @param code
 * @returns {Promise<unknown>}
 */
function login2fa(email, code) {
    const csrf = getCookie('csrftoken');

    return new Promise(function (resolve, reject) {
        request
            .post(url('/login/login_2fa/'))
            .type('json')
            .accept('json')
            .set('X-CSRFToken', csrf)
            .send({
                email: email,
                code: code
            })
            .end(function (err, res) {
                if (err || !res || !res.body.token) {
                    reject((res && res.body) || {});
                } else {
                    setToken(res.body);
                    handleAuthStatusChange(true);
                    resolve();
                }
            });
    });
}

function logout() {
    setToken({ token: '', level: '', is_staff: false, partner_code: '' });
    handleAuthStatusChange(false);
}

function onAuthStatusChange(handler) {
    self.authStatusHandlers.push(handler);
}

function isAuthenticated() {
    return !!getToken();
}

/**
 * Helper function to get the value of a given cookie, useful to get the CSRF
 * token from Django when set.
 */
function getCookie(name) {
    let cookieValue;

    if (document.cookie && document.cookie !== '') {
        const cookies = document.cookie.split(';');

        for (let i = 0; i < cookies.length; i += 1) {
            const cookie = cookies[i].trim();

            if (cookie.substring(0, name.length + 1) === name + '=') {
                cookieValue = decodeURIComponent(
                    cookie.substring(name.length + 1)
                );
                break;
            }
        }
    }

    return cookieValue;
}

/**
 * Sets the headers for the request. If there is a CSRF token set in the
 * cookies, Django is going to expect it and so we add it to the X-CSRFToken
 * header.
 */
function prepare(r) {
    const csrf = getCookie('csrftoken');
    let out = r.accept('json').set('Authorization', `Token ${getToken()}`);

    if (csrf) {
        out = out.set('X-CSRFToken', csrf);
    }

    return out;
}

function forwardResult(r, test) {
    const parts = test.split('.');
    parts.unshift('body');

    return new Promise((resolve, reject) => {
        r.end((err, res) => {
            let ptr = res;

            if (err) {
                reject('Erreur inconnue');
            }

            while (parts.length) {
                const k = parts.shift();
                if (
                    !ptr[k] ||
                    (parts.length > 0 && typeof ptr[k] !== 'object')
                ) {
                    reject('Erreur inconnue');
                    return;
                }
                ptr = ptr[k];
            }

            resolve(res.body);
        });
    });
}

/**
 * Instead of just returning forwardResult when it worked, this turns a
 * request into a promise. If the request worked then the body is returned and
 * otherwise the response is raised as exception.
 */
function promisify(r) {
    return new Promise((resolve, reject) => {
        r.end((err, res) => {
            if (err) {
                reject(res);
            } else {
                resolve(res.body);
            }
        });
    });
}

export default {
    setToken,
    getToken,
    getLevel,
    isStaff,
    getPartnerCode,
    url,
    login,
    login2fa,
    isAuthenticated,
    logout,
    onAuthStatusChange,
    prepare,
    forwardResult,
    promisify
};



// WEBPACK FOOTER //
// ./src/api/core.js